import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, SimpleChanges } from '@angular/core';
import { CustomizerComponentHeaderComponent, CustomizerListComponent } from '@app/customizer/components';
import { ToggleComponent } from '@app/shared/components/toggle/toggle.component';
import { IconInfoComponent } from '@app/shared/icons/components/icon-info.component';
import { ComponentVariantModel } from '@models/component-variant';
import { VariantSizeModel } from '@models/variant-size';
import { TranslatePipe } from '@ngx-translate/core';
import { ConfigurationService } from '@services/configuration.service';
import { DataApiService } from '@services/data-api.service';
import { EventsService } from '@services/events.service';
import { ImageService } from '@services/image.service';
import { ComponentVariantChangeModel } from '../../../../_models/component-variant-change';
import { SorterService } from '../../../../_services/sorter.service';
import { FadeInOutTransition } from '../../../../shared/transitions/fade-in-out.transition';
import { BaseCustomizerComponent } from './../../../../shared/base-customizer-component';

@Component({
	selector: 'clip-length',
	styleUrls: ['./clip-length.component.scss'],
	animations: [
		FadeInOutTransition()
	],
	template: `

<div class="flex flex-align-end" >
  <customizer-component-header [menu]="menu" (updateCollapsedOnComponent)="updateCollapsed($event)" [overrideTranslateName]="'CUSTOMIZER.COMPONENTS.CLIPLENGTH.NAME'"></customizer-component-header>
  <icon-info (click)="showInfo = !showInfo" class="header-icon"></icon-info>
</div>
@if (items.length) {
  <div>
    @if (getSelectedVariant.SplittingAllowed && variants.length > 1) {
      <div class="form-group has-padding-horizontal-30px">
        <cstmsr-toggle [on]="getSelectedVariant.SplittingData !== null" [color]="'#E23F3F'" (valueChanged)="toggle($event)">
          {{ 'CUSTOMIZER.COMPONENTS.CLIPLENGTH.DIFFERENTLENGTHS' | translate }}
        </cstmsr-toggle>
      </div>
    }
    @if (!getSelectedVariant.SplittingData) {
      <customizer-list
        class="bordered"
        [selectedIndex]="selectedIndex"
        (selectedChange)="onSelect($event)"
        [items]="items"
        [name]="'Size'"
        [discontinued]="'Discontinued'"
        [(scrollPosition)]="menu.menuDetails.scrollPosition"
        [editMode]="state === customizerStateEnum.EDITORDER"
        [variantOrModelIsNew]="variantOrModelIsNew"
        [image]="getVoid"
      ></customizer-list>
    }
    @if (getSelectedVariant.SplittingData) {
      <div class="different-lengths">
        <div>
          <span class="side">Left</span>
          @for (size of items; track size) {
            <div class="customizer-item" (click)="setSize('left', size)">
              <div class="snip" [ngClass]="{'is-selected': size.Size === getSelectedVariant.SplittingData.left}"></div>
              <div class="name">{{size.Size}}</div>
            </div>
          }
        </div>
        <div>
          <span class="side">Right</span>
          @for (size of items; track size) {
            <div class="customizer-item" (click)="setSize('right', size)">
              <div class="snip" [ngClass]="{'is-selected': size.Size === getSelectedVariant.SplittingData.right}"></div>
              <div class="name">{{size.Size}}</div>
            </div>
          }
        </div>
      </div>
    }
  </div>
}
@if (showInfo) {
  <div [@fadeInOutTransition] class="spacer flex flex-column flex-center">
    <h6>{{ 'CUSTOMIZER.COMPONENTS.CLIPLENGTH.INFO' | translate }}</h6>
    <img class="illustration-image" [src]="infoImage()" [alt]="'CUSTOMIZER.COMPONENTS.CLIPLENGTH.INFO' | translate" />
  </div>
}
`,
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CommonModule, TranslatePipe, CustomizerListComponent, CustomizerComponentHeaderComponent, IconInfoComponent, ToggleComponent]
})

export class ClipLengthComponent extends BaseCustomizerComponent implements OnInit {

	public items: Array<VariantSizeModel> = [];
	public selectedIndex: number = 0;
	public sizeGroups: Array<any> = [];
	public showInfo: boolean = false;

	constructor(
		public override events: EventsService,
		public apiService: DataApiService,
		public imageService: ImageService,
		private sorter: SorterService,
		public override configurationService: ConfigurationService
	) {
		super(events, apiService, configurationService);
	}

	async initData() {
		this.events.showLoader(true);
		await this.fetchData();
		this.events.showLoader(false);
	}

	async onChanges(changes: SimpleChanges) {
		this.selectedIndex = this.items.findIndex(x => x.Uuid == this.getSelectedVariant.VariantSize?.Uuid);

		this.events.showLoader(true);
		await this.fetchData();
		this.events.showLoader(false);
	}

	public async fetchData() {
		this.items = await this.fetch() ?? [];
		this.selectedIndex = this.items.findIndex(x => x.Uuid == this.getSelectedVariant.VariantSize?.Uuid);
	}

	public infoImage = () => this.imageService.GetComponentInfoImage(this.getSelectedVariant, 'size');

	setSize(side: string, size: VariantSizeModel) {
		this.events.showLoader(true);

		const newVariant = Object.assign(new ComponentVariantModel(this.getSelectedVariant.Discontinued, this.getSelectedVariant.New), this.getSelectedVariant);
		newVariant.VariantSize = size;

		if (side == 'left') {
			newVariant.SplittingData = { left: size.Size, right: newVariant.SplittingData?.right };
		} else {
			newVariant.SplittingData = { left: newVariant.SplittingData?.left, right: size.Size };
		}

		this.updateComponentSize(new ComponentVariantChangeModel(this.getSelectedVariant, newVariant, null));
	}

	toggle(event: boolean) {
		this.events.showLoader(true);

		const newVariant = Object.assign(new ComponentVariantModel(this.getSelectedVariant.Discontinued, this.getSelectedVariant.New), this.getSelectedVariant);
		newVariant.VariantSize = this.items[this.selectedIndex];

		if (event) {
			newVariant.SplittingData = { left: newVariant.VariantSize.Size, right: newVariant.VariantSize.Size };
		} else {
			newVariant.SplittingData = null;
		}

		this.updateComponentSize(new ComponentVariantChangeModel(this.getSelectedVariant, newVariant, null));
	}

	onSelect(index: number) {
		this.events.showLoader(true);

		const newVariant = Object.assign(new ComponentVariantModel(this.getSelectedVariant.Discontinued, this.getSelectedVariant.New), this.getSelectedVariant);
		const oldVariant = Object.assign(new ComponentVariantModel(this.getSelectedVariant.Discontinued, this.getSelectedVariant.New), this.getSelectedVariant);
		newVariant.VariantSize = this.items[index];
		this.updateComponentSize(new ComponentVariantChangeModel(oldVariant, newVariant, null));
	}

	async fetch(): Promise<Array<VariantSizeModel> | undefined> {
		const variant = this.getSelectedVariant;
		variant.VariantSizeViewed = true;
		const apiData = await this.configurationService.getVariants(this.configuration?.ModelConfigurationUuid, this.modelGroup, undefined, variant.Uuid);
		await this.RunDisablingSystem(apiData);

		const sizes = apiData?.[0].VariantSizes.filter(x => !x.isDisabled);

		return sizes?.sort(this.sorter.sortBy(VariantSizeModel));
	}
	getVoid = () => {
		return '';
	}

}
